import Phaser from "phaser";
import { getAgritechText } from "../utils/agritechText";

export default class TitleScene extends Phaser.Scene {
  constructor() {
    super("TitleScene");
  }

  init(data) {
    this.revealFrom = data?.revealFrom || null;
  }

  preload() {
    this.load.image("titleBG", "/assets/titleBG.png");
    this.load.audio("titleMusic", "/assets/titleScreen.mp3");
    this.load.audio("tap", "/assets/tap.m4a");
    this.load.audio("selected", "/assets/selected.m4a");
  }

  create() {
    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "hidden",
          hideRightPanel: false,
          visibleControls: [],
          powerStates: {
            one: false,
            two: false,
            three: false,
          },
        },
      }),
    );

    const controlsUI = document.getElementById("main-scene-controls-ui");
    if (controlsUI) controlsUI.remove();

    const rightPanel = document.getElementById("right-ui-panel");

    if (rightPanel) {
      rightPanel.style.visibility = "visible";

      const existingLogo = rightPanel.querySelector(".right-panel-logo-area");

      if (!existingLogo) {
        rightPanel.insertAdjacentHTML(
          "afterbegin",
          `
        <div class="right-panel-logo-area">
          <img
            src="/assets/Ruta-N.png"
            alt="Ruta N"
            class="ruta-n-logo"
          />
        </div>
      `,
        );
      }
    }

    const gameplayContent = document.getElementById(
      "right-panel-gameplay-content",
    );

    if (gameplayContent) {
      gameplayContent.style.display = "none";
    }

    const leftPanel = document.getElementById("left-ui-panel");
    if (leftPanel) {
      leftPanel.innerHTML = `
    <div class="developer-logo-content">
      <p class="developer-logo-label">
        Desarrollado por:
      </p>

      <img
        src="/assets/CES.png"
        alt="CES University"
        class="institution-logo"
      />

      <p class="developer-logo-label">
        En el marco de:
      </p>

      <img
        src="/assets/futumed.png"
        alt="Futumed"
        class="futumed-logo"
      />
    </div>
  `;
    }

    this.titleMusic = this.sound.add("titleMusic", {
      loop: true,
      volume: Number(localStorage.getItem("agritech-music-volume") ?? "0.4"),
    });

    this.titleMusic.play();

    this.add.image(400, 300, "titleBG").setDisplaySize(800, 600);

    // Keep the background visible while improving text readability
    this.add.rectangle(400, 300, 800, 600, 0x06130c, 0.38).setDepth(1);

    const lang = localStorage.getItem("agritech-language") || "es";
    const t = getAgritechText();

    const gameTitle = lang === "es" ? "Verde Origen" : "Green Origin";
    const historyLabel = lang === "es" ? "Historia" : "History";

    // MAIN TITLE
    this.add
      .text(400, 82, gameTitle, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "56px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 7,
        shadow: {
          offsetX: 0,
          offsetY: 5,
          color: "#020d07",
          blur: 10,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(3);

    // TAGLINE
    this.add
      .text(400, 137, t.titleTagline, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "18px",
        color: "#fde68a",
        fontStyle: "600",
        letterSpacing: 1.5,
        shadow: {
          offsetX: 0,
          offsetY: 2,
          color: "#000000",
          blur: 5,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(3);

    // Small decorative accent underneath the tagline
    this.add.rectangle(400, 164, 130, 3, 0xa3e635, 0.9).setDepth(3);

    const makeButton = (y, label, callback, isPrimary = false) => {
      const baseColor = isPrimary ? 0x4d7c0f : 0x0b1510;
      const hoverColor = isPrimary ? 0x65a30d : 0x18291f;
      const borderColor = isPrimary ? 0xd9f99d : 0x6f8f5b;

      // Subtle depth behind the button
      this.add.rectangle(400, y + 4, 264, 48, 0x000000, 0.32).setDepth(2);

      const button = this.add
        .rectangle(400, y, 264, 48, baseColor, isPrimary ? 0.98 : 0.9)
        .setStrokeStyle(isPrimary ? 3 : 2, borderColor)
        .setDepth(3)
        .setInteractive({ useHandCursor: true });

      const text = this.add
        .text(400, y, label, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: isPrimary ? "20px" : "18px",
          color: isPrimary ? "#ffffff" : "#f8fafc",
          fontStyle: "bold",
        })
        .setOrigin(0.5)
        .setDepth(4);

      button.on("pointerover", () => {
        this.sound.play("tap", {
          volume:
            Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
            0.45,
        });

        button.setFillStyle(hoverColor, 1);
        text.setColor(isPrimary ? "#ffffff" : "#d9f99d");
        button.setScale(1.025);
        text.setScale(1.025);
      });

      button.on("pointerout", () => {
        button.setFillStyle(baseColor, isPrimary ? 0.98 : 0.9);
        text.setColor(isPrimary ? "#ffffff" : "#f8fafc");
        button.setScale(1);
        text.setScale(1);
      });

      button.on("pointerdown", () => {
        this.sound.play("selected", {
          volume:
            Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
            0.5,
        });

        callback();
      });
    };

    makeButton(
      205,
      t.playGame,
      () => {
        this.input.enabled = false;
        window.dispatchEvent(new Event("agritech-open-player-modal"));
      },
      true,
    );

    this.startGameAfterPlayerInfo = () => {
      this.input.enabled = true;

      if (
        document.activeElement &&
        typeof document.activeElement.blur === "function"
      ) {
        document.activeElement.blur();
      }

      this.input.keyboard.resetKeys();

      if (this.titleMusic) {
        this.titleMusic.stop();
      }

      this.scene.start("TransitionScene", {
        label: t.gameStart,
        nextScene: "MainScene",
      });
    };

    window.addEventListener(
      "agritech-player-ready",
      this.startGameAfterPlayerInfo,
    );

    this.enableTitleInputAfterModalClosed = () => {
      this.input.enabled = true;
    };

    window.addEventListener(
      "agritech-player-modal-closed",
      this.enableTitleInputAfterModalClosed,
    );

    this.events.once("shutdown", () => {
      window.removeEventListener(
        "agritech-player-ready",
        this.startGameAfterPlayerInfo,
      );

      window.removeEventListener(
        "agritech-player-modal-closed",
        this.enableTitleInputAfterModalClosed,
      );
    });

    makeButton(265, t.tutorial, () => {
      if (this.titleMusic) {
        this.titleMusic.stop();
      }

      this.scene.start("TransitionScene", {
        label: t.tutorialStart,
        nextScene: "TutorialScene",
      });
    });

    makeButton(325, historyLabel, () => {
      if (this.titleMusic) {
        this.titleMusic.stop();
      }

      this.scene.start("HistoryScene");
    });

    makeButton(385, t.options, () => {
      this.scene.launch("OptionsScene");
      this.scene.pause("TitleScene");
    });

    makeButton(445, t.credits, () => {
      this.scene.launch("CreditsScene");
      this.scene.pause("TitleScene");
    });

    makeButton(505, t.leaderboard, () => {
      this.scene.launch("LeaderboardScene");
      this.scene.pause("TitleScene");
    });

    this.input.keyboard.once("keydown-ZERO", () => {
      if (!this.input.enabled) return;

      if (this.titleMusic) {
        this.titleMusic.stop();
      }

      const testScore = 1000;

      const rightPanel = document.getElementById("right-ui-panel");
      if (rightPanel) rightPanel.style.visibility = "visible";

      const scoreEl = document.getElementById("ui-score");
      const starCountEl = document.getElementById("ui-star-count");
      const starScoreEl = document.getElementById("ui-star-score");

      if (scoreEl) scoreEl.textContent = this.score;
      if (starCountEl) starCountEl.textContent = this.starCount;
      if (starScoreEl) starScoreEl.textContent = this.starScore;

      this.scene.start("TransitionScene", {
        label: getAgritechText().finish,
        nextScene: "ResultScene",
        nextData: {
          score: testScore,
        },
      });
    });
    if (this.revealFrom) {
      const blackOverlay = this.add
        .rectangle(400, 300, 800, 600, 0x000000, 1)
        .setDepth(9999);

      const revealGraphics = this.make.graphics({ x: 0, y: 0, add: false });
      const revealMask = revealGraphics.createGeometryMask();
      revealMask.setInvertAlpha(true);

      blackOverlay.setMask(revealMask);

      const reveal = { radius: 1 };

      this.tweens.add({
        targets: reveal,
        radius: 1000,
        duration: 1200,
        ease: "sine.inOut",
        onUpdate: () => {
          revealGraphics.clear();
          revealGraphics.fillStyle(0xffffff, 1);
          revealGraphics.fillCircle(
            this.revealFrom.x,
            this.revealFrom.y,
            reveal.radius,
          );
        },
        onComplete: () => {
          blackOverlay.clearMask();
          revealGraphics.destroy();
          blackOverlay.destroy();
        },
      });
    }
  }
}
